import React, { useCallback, useEffect, useState } from "react";
import { Add } from "@mui/icons-material";
import { TextField, Box, Button, useTheme } from "@mui/material";
import { OralList } from "../../../../../components/oral/oral-list";
import { clearOralQuery, getOralQuery, setOralQuery } from "../../../../../services/local-session-storage";
import { OralModel } from "../../../../../models/Oral.model";
import { QueryObservations } from "../../observation/query-observations";
import { handlePromise } from "../../../../../utils/handlePromisse";
import { consultationOral, createBatchOralConsultation, BatchOralConsultationRequest } from "../../../../../services/oralService";
import { generatePdfOral, openHtmlForPrint } from "../../../../../utils/pdf.util";
import { SimpleSnackbar } from "../../../../../components/snackbar";
import { MessageDialog } from "../../../../../components/message-dialog";
import { useNavigate } from "react-router-dom";
import { QueryState } from "../../index";
import { Loading } from "../../../../../components/loading";
import { oralModelToPreview, Preview } from "../../components/preview/preview";
import WizardShell from "../../../../../components/ui/WizardShell";
import { ORAL_STEPS } from "../../wizard-steps";


type Props = {
    onBack?: () => void
}

export const DosageOral: React.FC<Props> = ({}) => {
    const theme = useTheme()
    const model = getOralQuery()
    const navigate = useNavigate()
    const [nameConsultation, setNameConsultation] = useState<string>(model.name || '')
    const [actives, setActives] = useState<OralModel[]>([])
    const [observations, setObservations] = useState<string[]>(model.observations || [])
    const [openObservations, setOpenObservations] = useState(false)
    const [showSnackbar, setShowSnackbar] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState('')
    const [showAnotherConsult, setShowAnotherConsult] = useState(false)
    const [loading, setLoading] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(false)
    const [open, setOpen] = useState(false)

    const onDeleteItem = useCallback((id: number) => {
        setActives(prev => prev.filter((product) => product.id !== id))
    }, [setOralQuery, actives])

    const handleName = useCallback((name: string) => {
        setNameConsultation(name)
        setOralQuery({name: name})
    },[setOralQuery])

    const handleConfirmDialog = useCallback(() => {
        setOpen(false)
    }, [setOpen])

    const savePdf = useCallback(async (action: 'done' | 'pdf' | undefined) => {
        setLoading(true);
        try {
            const query = getOralQuery();
            const filteredActives = query.actives.map(item => ({
                active: item.id,
                unit: item.unity,
                volume: item.volume || 0,
            }));

            let consultationIds: number[] = [];
            let totalCreated = 0;
            let totalFailed = 0;

            // Se tiver múltiplos pacientes, usar endpoint v2 batch
            if (query.patientIds && query.patientIds.length > 0) {
                const batchActives = filteredActives
                    .filter(item => item.active !== undefined)
                    .map(item => ({
                        active: item.active as number,
                        volume: Number(item.volume) || 0,
                        unit: item.unit || '',
                    }));

                const batchRequest: BatchOralConsultationRequest = {
                    patients: query.patientIds,
                    consultation: {
                        name: query.name || '',
                        actives: batchActives,
                        observations: query.observations,
                    }
                };

                const [batchResult, batchError] = await handlePromise(createBatchOralConsultation(batchRequest));

                if (batchError) {
                    setSnackbarMessage('Erro ao salvar as consultas orais');
                    setShowSnackbar(true);
                    setTimeout(() => navigate('/'), 500);
                    return;
                }

                totalCreated = batchResult.data.summary.created;
                totalFailed = batchResult.data.summary.failed;

                const successResults = batchResult.data.results.filter(r => r.consultationId !== undefined);
                if (successResults.length > 0) {
                    consultationIds = successResults.map(r => r.consultationId as number);
                } else {
                    setSnackbarMessage('Nenhuma consulta foi criada com sucesso');
                    setShowSnackbar(true);
                    return;
                }

                if (totalFailed > 0) {
                    setSnackbarMessage(`${totalCreated} consultas criadas, ${totalFailed} falharam`);
                } else {
                    setSnackbarMessage(action === 'pdf' ? 'Gerando PDF da consulta oral...' : `${totalCreated} consultas orais salvas com sucesso!`);
                }
                setShowSnackbar(true);

            } else {
                // Endpoint tradicional para um único paciente
                const consult = {
                    name: query.name,
                    actives: filteredActives,
                    observations: query.observations,
                    patient: query.patient === 0 ? undefined : query.patient,
                };

                const [saveConsult, error] = await handlePromise(consultationOral(consult));

                if (error) {
                    setSnackbarMessage('Erro ao salvar a consulta oral');
                    setShowSnackbar(true);
                    setTimeout(() => navigate('/'), 500);
                    return;
                }

                if ('consultations' in saveConsult.data && saveConsult.data.consultations) {
                    consultationIds = saveConsult.data.consultations.map((c: { consultationId: number }) => c.consultationId);
                } else if ('consultation' in saveConsult.data) {
                    consultationIds = [saveConsult.data.consultation];
                } else {
                    setSnackbarMessage('Erro ao obter ID da consulta');
                    setShowSnackbar(true);
                    return;
                }

                setSnackbarMessage(action === 'pdf' ? 'Gerando PDF da consulta oral...' : 'Consulta oral salva com sucesso!');
                setShowSnackbar(true);
            }

            if (action === 'pdf') {
                const pdfData = {
                    consults: consultationIds.map((id) => ({ consultationId: id, type: 'ORAL' })),
                    pregnant: false,
                };

                const result = await generatePdfOral(pdfData);

                if (!result.success) {
                    setSnackbarMessage(result.error || 'Erro ao gerar o PDF da consulta oral');
                    setShowSnackbar(true);
                } else if (result.html) {
                    openHtmlForPrint(result.html);
                }
                // Se não retornou HTML (V2), o PDF já foi baixado
            }

            const hasPatients = !!model.patient || (!!model.patientIds && model.patientIds.length > 0);
            if (hasPatients) {
                setShowAnotherConsult(true);
            } else {
                setTimeout(() => navigate('/'), 500);
            }
        } catch (err) {
            setSnackbarMessage('Ocorreu um erro inesperado');
            setShowSnackbar(true);
        } finally {
            setLoading(false);
        }
    }, [model.patient, model.patientIds, navigate]);

    const handleFinishConsult = useCallback(async () => {
        clearOralQuery()
        setShowAnotherConsult(false)
        navigate('/dashboard/query')
    }, [setShowAnotherConsult, navigate])

    const handleAnotherConsult = useCallback(async () => {
        clearOralQuery()
        if (model.patientIds && model.patientIds.length > 0) {
            navigate('/dashboard/query', {
                state: { patientIds: model.patientIds } as QueryState,
            })
        } else if (model.patient) {
            navigate('/dashboard/query', {
                state: { patientId: model.patient } as QueryState,
            })
        } else {
            navigate('/dashboard/query')
        }
    }, [navigate])

    const handleBackButton = useCallback(() => {
        navigate('/dashboard/query', {
            state: { fromDoseOral: true } as QueryState,
        })
    }, [])

    const handleItemChange = useCallback((id: number, volume: string, unity: string) => {
        setActives(prevActives => {
            return prevActives.map(active => {
                if (active.id === id) {
                    return {
                        ...active,
                        volume: volume,
                        unity: unity,
                    };
                }
                return active;
            });
        });
    }, [setActives]);

    useEffect(() => {
        // Sem estado do wizard (aba nova, "voltar" depois de salvar, link direto): recomeça a consulta.
        if (!model.actives?.length) {
            navigate('/dashboard/query', { replace: true })
            return
        }
        setActives(model.actives)
    },[])

    useEffect(() => {
        setOralQuery({ actives })
    }, [actives])

    // Footer buttons for WizardShell — exibidos também no fluxo com múltiplos pacientes
    // (a receita é gerada para todas as consultas do lote; o preview mostra a fórmula compartilhada).
    const footerExtra = (
        <>
            <Button
                variant="text"
                onClick={() => setPreviewOpen(true)}
                sx={{ height: 48, px: 2.5, color: "secondary.main", backgroundColor: "background.paper", border: `1.5px solid ${theme.palette.divider}`, "&:hover": { backgroundColor: theme.palette.brand.surface2 } }}
            >
                Pré-visualizar
            </Button>
            <Button
                variant="text"
                onClick={() => savePdf('pdf')}
                sx={{ height: 48, px: 2.5, fontWeight: 600, color: "primary.main", backgroundColor: "background.paper", border: `1.5px solid ${theme.palette.primary.main}`, "&:hover": { backgroundColor: theme.palette.brand.surface2 } }}
            >
                Gerar receita
            </Button>
        </>
    )

    return (
        <WizardShell
            steps={[...ORAL_STEPS]}
            activeIndex={3}
            onBack={handleBackButton}
            onNext={() => savePdf('done')}
            nextLabel="Salvar"
            footerExtra={footerExtra}
        >
            <Loading open={loading}/>

            <QueryObservations
                open={openObservations}
                externalObservations={observations}
                onClose={() => setOpenObservations(false)}
                onConfirm={(observations) => {
                    setOpenObservations(false)
                    setObservations(observations)
                    setOralQuery({observations})
                }}
            />

            <SimpleSnackbar
                show={showSnackbar}
                message={snackbarMessage}
                onDismiss={() => setShowSnackbar(false)}
            />

            <MessageDialog
                open={showAnotherConsult}
                title={"Continuar formulando?"}
                message={"Deseja continuar formulando para o(s) mesmo(s) paciente(s)?"}
                onClose={handleFinishConsult}
                onConfirm={handleAnotherConsult}
                confirmButtonText={"Sim"}
                cancelButtonText={"Não"}/>

            <Preview
                observations={model.observations}
                patientId={model.patient}
                patientIds={model.patientIds}
                consultationName={nameConsultation}
                details={actives.map(oralModelToPreview)}
                previewOpen={previewOpen}
                onClose={() => setPreviewOpen(false)}
                onConfirm={handleConfirmDialog}
            />

            {/* Consultation name */}
            <TextField
                label={"Nome da consulta"}
                sx={{ width: '100%', mb: 3 }}
                color={"primary"}
                value={nameConsultation}
                onChange={(e) => {
                    handleName(e.target.value)
                }}
                inputProps={{
                    maxLength: 100,
                }}
                helperText={`${nameConsultation.length}/${100}`}
            />

            {/* Actives list */}
            <OralList
                products={actives}
                onRemoveItem={onDeleteItem}
                onItemChange={handleItemChange}
            />

            {/* Observations button */}
            <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() => setOpenObservations(true)}
                sx={{
                    mt: 2,
                    height: 44,
                    px: 2.5,
                    borderColor: theme.palette.divider,
                    color: "text.secondary",
                }}
            >
                Observações
            </Button>
        </WizardShell>
    )
}
